import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Button, Checkbox, Divider, FormControlLabel, Grid, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import moment from 'moment';
import Modal from 'components/shared/Modal';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { defaultFormProps, getAddressFormValues, getValidationProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import { ModalProps } from 'store/types/ComponentProps';
import UserService from 'services/api/UserService';
import CompanyService from 'services/api/CompanyService';
import { useSnackbar } from 'notistack';
import UserNameFormSection from 'components/profile/UserNameFormSection';
import EmailFormItem from 'components/profile/EmailFormItem';
import DateFormItem from 'components/shared/DateFormItem';
import PhoneFormItem from 'components/profile/PhoneFormItem';
import AddressFormSection from 'components/shared/AddressFormSection';
import FileUploadFormItem from 'components/shared/FileUploadFormItem';
import { CompanyUserFormValues } from 'store/types/FormValues';
import Address from 'store/types/Address';
import { ConfigContext } from 'components/ConfigGuard';
import { ParametersContext } from 'components/ParametersGuard';
import TitleFormItem from 'components/profile/TitleFormItem';
import SelectOption from 'store/types/SelectOption';
import { MemberClassification } from 'store/types/Company';

import styles from './AddCompanyUserModal.module.scss';

interface AddCompanyUserModalProps extends ModalProps {
  companyId: string;
  portalRolesList: SelectOption[];
  refetch: () => void;
  predefinedAddress?: Address;
  memberClassification?: MemberClassification[];
}

type AddCompanyUserFormValues = CompanyUserFormValues & {
  isEmployee: boolean;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const getDefaultValues = (hideDirectoryStatusId?: string, isEmployee = false): AddCompanyUserFormValues => ({
  firstName: '',
  lastName: '',
  middleInitial: '',
  email: '',
  address: getAddressFormValues({ country: 'US' }),
  phone: '',
  title: '',
  birthDate: '',
  directoryNetworks: [],
  directoryStatus: hideDirectoryStatusId ?? '',
  functionIds: [],
  files: [],
  isEmployee,
});

const AddCompanyUserModal: React.FunctionComponent<AddCompanyUserModalProps> = ({
  open,
  onClose,
  companyId,
  portalRolesList,
  refetch,
  predefinedAddress,
}) => {
  const defaultValues = useMemo(() => getDefaultValues(), []);
  const form = useForm<AddCompanyUserFormValues>({ ...defaultFormProps, defaultValues });

  const {
    modulesSettings: {
      companyManagement: { enableContactBirthdate, enableContactFileUpload },
    },
  } = useContext(ConfigContext);
  const {
    companyManagement: { addPersonModalHeader: addAdminModalHeader },
  } = useContext(ParametersContext);
  const {
    handleSubmit,
    formState: { isValid, errors },
    control,
    setValue,
    watch,
    reset,
    trigger,
  } = form;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);

  const { isEmployee } = watch();

  useEffect(() => {
    if (predefinedAddress) {
      setValue('address', predefinedAddress);
    }
  }, [predefinedAddress, setValue]);

  useEffect(() => {
    void trigger('functionIds');
  }, [isEmployee, trigger]);

  const handleAddUserSubmit = useCallback(
    async ({ files = [], ...userValues }: AddCompanyUserFormValues) => {
      setLoading(true);

      try {
        const response = await CompanyService.addCompanyUser(userValues, companyId);

        if (enableContactFileUpload && response.customerId && files.length > 0) {
          try {
            await UserService.uploadFile(response.customerId, files[0]);
          } catch (errorMessage) {
            enqueueSnackbar('The user was added, but the file could not be uploaded.', defaultSnackbarErrorProps);

            onClose();
            refetch();
            reset(defaultValues);
            return;
          }
        }

        enqueueSnackbar('User successfully added', {
          variant: 'success',
        });

        onClose();
        refetch();
        reset(defaultValues);
      } catch (errorMessage) {
        enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
      } finally {
        setLoading(false);
      }
    },
    [companyId, defaultValues, enableContactFileUpload, enqueueSnackbar, onClose, refetch, reset]
  );

  const handleClose = useCallback(() => {
    onClose();
    reset(defaultValues);
  }, [defaultValues, onClose, reset]);

  return (
    <FormProvider {...form}>
      <Modal
        open={open}
        onClose={handleClose}
        maxWidth={'md'}
        title={'Create New Person'}
        loading={loading}
        actions={
          <>
            <div>
              <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
                {'Cancel'}
              </Button>
            </div>
            <Button
              color={'secondary'}
              variant={'contained'}
              type={'submit'}
              disabled={!isValid}
              onClick={handleSubmit(handleAddUserSubmit)}
            >
              {'Save'}
            </Button>
          </>
        }
      >
        <p className={styles.text} dangerouslySetInnerHTML={{ __html: addAdminModalHeader }} />
        <h1 className={styles.title}>{'New Account Information'}</h1>
        <Divider className={styles.divider} />
        <Grid {...defaultGridContainerProps}>
          <UserNameFormSection middleNameVisible={true} nicknameVisible={true} />
          <Grid {...defaultGridItemProps} md={6}>
            <EmailFormItem required />
          </Grid>
          <Grid {...defaultGridItemProps} md={6}>
            <PhoneFormItem />
          </Grid>
          <Grid {...defaultGridItemProps} md={6}>
            <TitleFormItem />
          </Grid>
          <AddressFormSection />
          {enableContactBirthdate && (
            <Grid {...defaultGridItemProps} md={6}>
              <DateFormItem
                fieldName={'birthDate'}
                label={'Date of Birth'}
                maxDate={moment(new Date()).startOf('day')}
              />
            </Grid>
          )}
          {enableContactFileUpload && (
            <Grid {...defaultGridItemProps}>
              <FileUploadFormItem></FileUploadFormItem>
            </Grid>
          )}
          <Grid {...defaultGridItemProps}>
            <FormControlLabel
              label={'Is Employee?'}
              control={
                <Controller
                  name={'isEmployee'}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      color={'primary'}
                      disabled={loading}
                      inputRef={field.ref}
                      onBlur={field.onBlur}
                      onChange={(_event, checked) => field.onChange(checked)}
                    />
                  )}
                />
              }
            />
          </Grid>
          <Grid {...defaultGridItemProps} xl={6}>
            <Controller
              name={'functionIds'}
              control={control}
              rules={{
                validate: (functionIds: string[] = []) =>
                  isEmployee || functionIds.length > 0 || 'Please input portal role',
              }}
              render={({ field }) => {
                const selectedOptions = portalRolesList.filter(({ id }) => field.value?.includes(id));

                return (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    disabled={loading}
                    options={portalRolesList}
                    value={selectedOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onBlur={field.onBlur}
                    onChange={(_event, selectedRoles) => {
                      field.onChange(selectedRoles.map(({ id }) => id));
                    }}
                    ChipProps={{
                      color: 'secondary',
                      variant: 'filled',
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} checkedIcon={checkedIcon} icon={icon} sx={{ mr: 1 }} />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...getValidationProps('functionIds', errors)}
                        inputRef={field.ref}
                        label={'Portal Roles'}
                        required={!isEmployee}
                        placeholder={selectedOptions.length ? undefined : 'Select one or more Portal Roles'}
                      />
                    )}
                  />
                );
              }}
            />
          </Grid>
        </Grid>
      </Modal>
    </FormProvider>
  );
};

export default AddCompanyUserModal;
